import { useCallback, useEffect } from 'react';
import {
  intlNumberFormatter,
  useFormattedInput,
  type IntlNumberFormatterOptions,
} from 'react-formatted-input-hook';

import { useNumberLocalize } from './locales/useNumberLocalize.js';

export interface UseFormattedNumberInputProps extends Omit<
  React.ComponentProps<'input'>,
  'size' | 'color' | 'value' | 'onChange'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  color?: 'default' | 'warning' | 'error';
  size?: 'small' | 'medium';
  maxDecimals?: number;
  minDecimals?: number;
  restFormatProps?: Omit<
    IntlNumberFormatterOptions,
    'location' | 'value' | 'onChange'
  >;
  /**
   * TODO(PDT-102307): remove this prop together with the
   * `decimalFormattingOnCellEdit` feature flag.
   */
  preserveRawValueOnEdit?: boolean;
}

export const useFormattedNumberInput = (
  props: UseFormattedNumberInputProps,
) => {
  const locale = useNumberLocalize();

  const {
    value,
    onChange,
    onFocus,
    onBlur,
    maxDecimals = 2,
    restFormatProps,
    preserveRawValueOnEdit,
    autoFocus,
    ...restProps
  } = props;

  const formattedInput = useFormattedInput(
    intlNumberFormatter({
      ...restFormatProps,
      ...(preserveRawValueOnEdit ? { minDecimals: 0 } : {}),
      locales: locale,
      value,
      onChange,
      maxDecimals,
      liveUpdate: true,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    ...rest
  } = formattedInput.props;

  // A workaround for the issue with the autoFocus prop. You can read more about it here: nick-keller/react-formatted-input-hook#3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return {
    ref,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...rest,
    ...restProps,
  };
};
